import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { estrellas } from "../../funcionesDatos/Textos";
import BloqueResenaCompletada from "../../componentesEspeciales/reservasAdmin/bloqueResenaCompletada";

// /admin/resenas — seguimiento de reseñas. Datos: POST /admin/resenas (backend/routes/administracion.js).
// - Pestaña "Reseñas": todas las reseñas con filtros (estrellas, solo con comentario, texto, vendedor/servicio).
// - Pestaña "Sin reseña": reservas completadas cuyo comprador no reseñó, con el botón de recordatorio manual.
// - Arriba: promedio, distribución por estrellas y % de reservas completadas con reseña del periodo.

const PERIODOS = [
    { key: 7,      label: "7 días" },
    { key: 30,     label: "30 días" },
    { key: 90,     label: "90 días" },
    { key: "todo", label: "Todo" },
];

const COLOR_ESTRELLAS = { 5: "var(--verde-bootstrap)", 4: "var(--verde-esmeralda)", 3: "var(--naranja-mapa)", 2: "var(--naranja-600)", 1: "var(--rojo-error)" };

function fmt(fecha) {
    if (!fecha) return "—";
    return new Date(fecha).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function nombreUsuario(u) {
    if (!u) return "—";
    return [u.nombre, u.apellido].filter((v) => v && v !== "NA").join(" ") || `@${u.username}`;
}

function abrirUsuario(e, u) {
    e.stopPropagation();
    if (!u?.username) return;
    localStorage.setItem("admin_nav_usuario", JSON.stringify({ username: u.username }));
    window.open("/admin/usuario", "_blank");
}

function Tile({ label, valor, sub, color, icono }) {
    return (
        <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body p-4">
                <div className="rounded-3 d-flex align-items-center justify-content-center mb-3" style={{ width: 44, height: 44, background: `${color}18` }}>
                    <i className={`bi ${icono} fs-5`} style={{ color }}></i>
                </div>
                <p className="text-secondary fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>{label}</p>
                <h3 className="fw-bold text-dark mb-1">{valor}</h3>
                {sub ? <p className="text-secondary mb-0" style={{ fontSize: "0.8rem" }}>{sub}</p> : null}
            </div>
        </div>
    );
}

function Resenas() {
    const navigate = useNavigate();

    const [tab, setTab]                     = useState("resenas");
    const [periodo, setPeriodo]             = useState(30);
    const [calificaciones, setCalificaciones] = useState([]);
    const [soloConComentario, setSoloConComentario] = useState(false);
    const [texto, setTexto]                 = useState("");
    const [vendedorOServicio, setVendedorOServicio] = useState("");
    const [textoAplicado, setTextoAplicado] = useState("");
    const [vendedorAplicado, setVendedorAplicado] = useState("");
    const [pagina, setPagina]               = useState(1);

    const [items, setItems]       = useState([]);
    const [total, setTotal]       = useState(0);
    const [paginas, setPaginas]   = useState(1);
    const [resumen, setResumen]   = useState(null);
    const [cargando, setCargando] = useState(false);

    // Las cajas de texto se aplican 400 ms después de dejar de escribir (no una consulta por tecla).
    useEffect(() => {
        const t = setTimeout(() => { setTextoAplicado(texto.trim()); setVendedorAplicado(vendedorOServicio.trim()); setPagina(1); }, 400);
        return () => clearTimeout(t);
    }, [texto, vendedorOServicio]);

    useEffect(() => {
        (async () => {
            try {
                setCargando(true);
                const res = await axios.post("/admin/resenas", { params: {
                    tab, pagina, periodo,
                    calificaciones,
                    soloConComentario,
                    texto: textoAplicado,
                    vendedorOServicio: vendedorAplicado,
                }});
                setItems(res.data.items || []);
                setTotal(res.data.total || 0);
                setPaginas(res.data.paginas || 1);
                setResumen(res.data.resumen || null);
            } catch {
                setItems([]); setTotal(0); setPaginas(1);
            } finally {
                setCargando(false);
            }
        })();
    }, [tab, pagina, periodo, calificaciones, soloConComentario, textoAplicado, vendedorAplicado]);

    function cambiarTab(nuevo) { setTab(nuevo); setPagina(1); }
    function cambiarPeriodo(p) { setPeriodo(p); setPagina(1); }
    function alternarCalificacion(n) {
        setCalificaciones((prev) => prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n].sort());
        setPagina(1);
    }
    function verReserva(idReserva) {
        if (!idReserva) return;
        navigate("/admin/reserva-admin", { state: { params: { idReserva } } });
    }
    // Tras el recordatorio manual, actualiza la fila sin recargar.
    function marcarRecordatorioEnviado(idReserva, datos) {
        setItems((prev) => prev.map((i) => String(i._id) === String(idReserva)
            ? { ...i, recordatorioResenaManualEn: datos?.recordatorioResenaManualEn, recordatoriosResenaManuales: datos?.recordatoriosResenaManuales }
            : i));
    }

    const r = resumen || { totalResenas: 0, promedio: 0, distribucion: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, completadas: 0, completadasConResena: 0, completadasSinResena: 0, porcentajeConResena: 0 };
    const maxDistribucion = Math.max(...Object.values(r.distribucion || {}), 1);
    const etiquetaPeriodo = periodo === "todo" ? "todo el tiempo" : `últimos ${periodo} días`;

    return (
        <div className="container-fluid">
            {/* Encabezado */}
            <div className="d-flex justify-content-between flex-wrap align-items-center gap-3 pt-3 pb-2 mb-4 border-bottom">
                <div>
                    <h1 className="h2 text-dark mb-1">Reseñas</h1>
                    <p className="text-secondary mb-0 small">Seguimiento de lo que opinan los compradores y de las reservas que todavía no reseñaron.</p>
                </div>
                <div className="d-flex gap-1">
                    {PERIODOS.map((op) => (
                        <button key={op.key} type="button"
                            className={`btn btn-sm rounded-pill px-3 ${periodo === op.key ? "btn-dark" : "btn-outline-secondary"}`}
                            style={{ fontSize: "0.78rem" }}
                            onClick={() => cambiarPeriodo(op.key)}>
                            {op.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Resumen */}
            <div className="row g-3 mb-3">
                <div className="col-6 col-lg-3">
                    <Tile label="Calificación promedio" valor={r.totalResenas ? `${r.promedio.toFixed(2)} ★` : "—"} sub={`Sobre ${r.totalResenas.toLocaleString("es-CO")} reseñas · ${etiquetaPeriodo}`} color="var(--ambar-600)" icono="bi-star-fill" />
                </div>
                <div className="col-6 col-lg-3">
                    <Tile label="Reseñas" valor={r.totalResenas.toLocaleString("es-CO")} sub={`${(r.distribucion?.[1] || 0) + (r.distribucion?.[2] || 0)} de 1-2 ★`} color="var(--azul-logo-claro)" icono="bi-chat-square-text-fill" />
                </div>
                <div className="col-6 col-lg-3">
                    <Tile label="Completadas con reseña" valor={`${r.porcentajeConResena}%`} sub={`${r.completadasConResena} de ${r.completadas} reservas completadas`} color="var(--verde-esmeralda)" icono="bi-check2-circle" />
                </div>
                <div className="col-6 col-lg-3">
                    <Tile label="Completadas sin reseña" valor={r.completadasSinResena.toLocaleString("es-CO")} sub="Para hacerles seguimiento" color="var(--rojo-600)" icono="bi-hourglass-split" />
                </div>
            </div>

            {/* Distribución por estrellas */}
            <div className="card border-0 shadow-sm rounded-4 mb-4">
                <div className="card-body p-4">
                    <h6 className="fw-bold text-dark mb-3" style={{ fontSize: "0.92rem" }}>
                        <i className="bi bi-bar-chart-fill me-2" style={{ color: "var(--ambar-600)" }}></i>
                        Distribución por estrellas — {etiquetaPeriodo}
                    </h6>
                    <div className="d-flex flex-column gap-2">
                        {[5, 4, 3, 2, 1].map((n) => {
                            const cantidad = r.distribucion?.[n] || 0;
                            const pct = r.totalResenas ? Math.round((cantidad / r.totalResenas) * 100) : 0;
                            return (
                                <button key={n} type="button" className="btn p-0 border-0 text-start w-100" onClick={() => { setTab("resenas"); setCalificaciones([n]); setPagina(1); }} title={`Ver solo las de ${n} ★`}>
                                    <div className="d-flex align-items-center gap-3">
                                        <span style={{ width: 92, color: "var(--ambar-600)", letterSpacing: 1, fontSize: "0.85rem", flexShrink: 0 }}>{estrellas(n)}</span>
                                        <div className="flex-grow-1" style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 8, overflow: "hidden" }}>
                                            <div style={{ width: `${Math.round((cantidad / maxDistribucion) * 100)}%`, height: "100%", background: COLOR_ESTRELLAS[n], borderRadius: 999, transition: "width 0.5s ease" }} />
                                        </div>
                                        <span className="text-secondary" style={{ width: 90, fontSize: "0.8rem", textAlign: "right", flexShrink: 0 }}>{cantidad.toLocaleString("es-CO")} · {pct}%</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Pestañas */}
            <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                    <button type="button" className={`nav-link ${tab === "resenas" ? "active fw-semibold" : ""}`} onClick={() => cambiarTab("resenas")}>
                        <i className="bi bi-star me-1"></i>Reseñas
                        <span className="badge rounded-pill bg-primary ms-2">{r.totalResenas}</span>
                    </button>
                </li>
                <li className="nav-item">
                    <button type="button" className={`nav-link ${tab === "sinResena" ? "active fw-semibold" : ""}`} onClick={() => cambiarTab("sinResena")}>
                        <i className="bi bi-hourglass-split me-1"></i>Sin reseña
                        <span className="badge rounded-pill bg-danger ms-2">{r.completadasSinResena}</span>
                    </button>
                </li>
            </ul>

            {/* Filtros */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                {tab === "resenas" ? (
                    <>
                        {[5, 4, 3, 2, 1].map((n) => (
                            <button key={n} type="button"
                                className={`btn btn-sm rounded-pill px-3 ${calificaciones.includes(n) ? "btn-warning" : "btn-outline-secondary"}`}
                                style={{ fontSize: "0.78rem" }}
                                onClick={() => alternarCalificacion(n)}>
                                {n} ★
                            </button>
                        ))}
                        <button type="button"
                            className={`btn btn-sm rounded-pill px-3 ${soloConComentario ? "btn-dark" : "btn-outline-secondary"}`}
                            style={{ fontSize: "0.78rem" }}
                            onClick={() => { setSoloConComentario((v) => !v); setPagina(1); }}>
                            <i className="bi bi-chat-left-text me-1"></i>Solo con comentario
                        </button>
                        <input type="text" className="form-control form-control-sm" style={{ maxWidth: 220, fontSize: "0.8rem" }}
                            placeholder="Buscar en el comentario…" value={texto} onChange={(e) => setTexto(e.target.value)} />
                    </>
                ) : null}
                <input type="text" className="form-control form-control-sm" style={{ maxWidth: 240, fontSize: "0.8rem" }}
                    placeholder="Vendedor o servicio…" value={vendedorOServicio} onChange={(e) => setVendedorOServicio(e.target.value)} />
                {(calificaciones.length || soloConComentario || texto || vendedorOServicio) ? (
                    <button type="button" className="btn btn-sm btn-link text-decoration-none" onClick={() => { setCalificaciones([]); setSoloConComentario(false); setTexto(""); setVendedorOServicio(""); setPagina(1); }}>
                        Limpiar filtros
                    </button>
                ) : null}
            </div>

            {/* Lista */}
            {cargando ? (
                <div className="text-center py-5"><span className="spinner-border text-primary" role="status" /></div>
            ) : (
                <>
                    <p className="text-secondary small mb-2">{total.toLocaleString("es-CO")} {tab === "resenas" ? (total === 1 ? "reseña" : "reseñas") : (total === 1 ? "reserva sin reseña" : "reservas sin reseña")}</p>

                    {items.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <i className={`bi ${tab === "resenas" ? "bi-star" : "bi-emoji-smile"} fs-2 d-block mb-2`}></i>
                            {tab === "resenas" ? "No hay reseñas con estos filtros." : "Todas las reservas completadas del periodo tienen reseña."}
                        </div>
                    ) : (
                        <div className="d-flex flex-column gap-2 mb-4">
                            {items.map((i) => i.tipo === "resena" ? (
                                <div key={i.idValoracion || i._id} className="card border-0 shadow-sm rounded-4" style={{ cursor: "pointer" }} onClick={() => verReserva(i.idReserva)}>
                                    <div className="card-body py-3 px-4">
                                        <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                                    <span style={{ color: "var(--ambar-600)", letterSpacing: 1 }}>{estrellas(i.calificacion)}</span>
                                                    <span className="fw-bold" style={{ color: COLOR_ESTRELLAS[i.calificacion] }}>{i.calificacion}/5</span>
                                                    <span className="fw-semibold text-dark">{i.servicio?.nombre || "Servicio eliminado"}</span>
                                                    {i.reserva?.reclamo?.abierto ? (
                                                        <span className="badge rounded-pill px-2 py-1" style={{ background: "rgba(var(--rojo-error-rgb), 0.1)", color: "var(--rojo-error)", fontSize: "0.72rem" }}>
                                                            <i className="bi bi-exclamation-triangle me-1"></i>Reclamo
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <p className="mb-2" style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                                                    {i.comentario ? i.comentario : <span className="text-muted fst-italic">Sin comentario.</span>}
                                                </p>
                                                <div className="text-muted small d-flex flex-wrap gap-3">
                                                    <span><i className="bi bi-calendar3 me-1"></i>{fmt(i.fecha || i.createdAt)}</span>
                                                    <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none small" onClick={(e) => abrirUsuario(e, i.comprador)}>
                                                        <i className="bi bi-bag me-1"></i>Comprador: {nombreUsuario(i.comprador)}
                                                    </button>
                                                    <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none small" onClick={(e) => abrirUsuario(e, i.vendedor)}>
                                                        <i className="bi bi-briefcase me-1"></i>Vendedor: {nombreUsuario(i.vendedor)}
                                                    </button>
                                                </div>
                                            </div>
                                            <i className="bi bi-chevron-right text-muted flex-shrink-0 mt-1"></i>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div key={i._id} className="card border-0 shadow-sm rounded-4" style={{ cursor: "pointer" }} onClick={() => verReserva(i._id)}>
                                    <div className="card-body py-3 px-4">
                                        <div className="d-flex align-items-start gap-3 flex-wrap">
                                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                                    <span className="fw-semibold text-dark">{i.servicio?.nombre || "Servicio eliminado"}</span>
                                                    <span className="badge rounded-pill px-2 py-1" style={{ background: "rgba(var(--gris-suave-rgb), 0.08)", color: "var(--gris-suave)", fontSize: "0.72rem" }}>{i.tipoPaquete}</span>
                                                    {i.recordatorioResenaEnviado ? (
                                                        <span className="badge rounded-pill px-2 py-1" style={{ background: "rgba(var(--azul-bootstrap-cta-rgb), 0.1)", color: "var(--azul-bootstrap-cta)", fontSize: "0.72rem" }}>
                                                            Recordatorio automático enviado
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <div className="text-muted small d-flex flex-wrap gap-3">
                                                    <span><i className="bi bi-check2-circle me-1"></i>Completada: {fmt(i.fechaCompletado)}</span>
                                                    <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none small" onClick={(e) => abrirUsuario(e, i.comprador)}>
                                                        <i className="bi bi-bag me-1"></i>Comprador: {nombreUsuario(i.comprador)}
                                                    </button>
                                                    <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none small" onClick={(e) => abrirUsuario(e, i.vendedor)}>
                                                        <i className="bi bi-briefcase me-1"></i>Vendedor: {nombreUsuario(i.vendedor)}
                                                    </button>
                                                </div>
                                            </div>
                                            <BloqueResenaCompletada reserva={i} alEnviar={marcarRecordatorioEnviado} />
                                            <i className="bi bi-chevron-right text-muted flex-shrink-0 mt-1"></i>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Paginación */}
                    {paginas > 1 ? (
                        <div className="d-flex justify-content-center align-items-center gap-3 mb-4">
                            <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill px-3" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
                                <i className="bi bi-chevron-left"></i> Anterior
                            </button>
                            <span className="small text-secondary">Página {pagina} de {paginas}</span>
                            <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill px-3" disabled={pagina >= paginas} onClick={() => setPagina((p) => p + 1)}>
                                Siguiente <i className="bi bi-chevron-right"></i>
                            </button>
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
}

export default Resenas;
